import clsx from 'clsx'
import { type ReactNode, useState } from 'react'

import {
  ClipboardButton,
  FvButton,
  FvLinkButton,
  ValidatedForm,
} from '@fv/client-components'
import type { UseMutateFunction } from '@fv/client-core'
import type { ListShipment, PropsWithClassName } from '@fv/client-types'

import { InputAdornment, InputGroup } from '../inputs/InputGroup'

type ReadOnly = {
  readOnly: true
}

type Editable = {
  readOnly?: false
  onSave: UseMutateFunction<ListShipment, unknown, string, unknown>
}

type Props = PropsWithClassName<{
  defaultValue?: string
  label: string
  loading: boolean
  name: string
  formClassName?: string
  inputGroupClassName?: string
  renderEndContentBefore?: (draftValue: string) => ReactNode
}> &
  (ReadOnly | Editable)

export const NOT_AVAILABLE = 'NA'

export const EditableRefNum = (props: Props) => {
  const {
    defaultValue = '',
    label,
    loading,
    name,
    readOnly = false,
    className,
    formClassName,
    inputGroupClassName,
    renderEndContentBefore,
  } = props
  const [isEditing, setEditing] = useState(false)
  const [draftValue, setDraftValue] = useState(defaultValue)

  function toggleEditing() {
    // Closing while loading can lead to confusing UI state
    setEditing(wasEditing => {
      const next = loading ? wasEditing : !wasEditing
      if (next && !wasEditing) {
        setDraftValue(defaultValue)
      }
      return next
    })
  }

  function onValidSubmit(form: HTMLFormElement) {
    if (loading) return

    const input = form.elements.namedItem(name) as HTMLInputElement
    const value = input.value.trim()

    if (value === defaultValue) toggleEditing()
    else if ('onSave' in props)
      props.onSave(value, { onSuccess: toggleEditing })
  }

  return (
    <div
      className={clsx(
        className,
        'flex items-center gap-x-2 b1350:flex-nowrap',
        {
          'flex-wrap': isEditing,
        },
      )}
    >
      {readOnly ? (
        <ReadonlyRefNum label={label} value={defaultValue} />
      ) : (
        <>
          <FvLinkButton
            className="break-all !whitespace-normal b1350:!whitespace-nowrap"
            theme="underlined"
            onClick={toggleEditing}
            title="Edit number"
          >
            <ReadonlyRefNum
              label={label}
              value={defaultValue}
              showCopy={false}
            />
          </FvLinkButton>
          <ClipboardButton value={defaultValue} />
        </>
      )}

      {isEditing && (
        <ValidatedForm
          className={`${formClassName} mb-4 mt-2 basis-full b1350:m-0 b1350:w-64`}
          onValidSubmit={onValidSubmit}
        >
          <InputGroup
            inputType="text"
            className={clsx('mb-0', inputGroupClassName)}
            inputProps={{
              autoFocus: true,
              name,
              onChange: e => setDraftValue(e.target.value),
              readOnly: loading,
              value: draftValue,
            }}
            endContent={
              <>
                {renderEndContentBefore?.(draftValue)}
                <InputAdornment position="end">
                  <FvButton
                    theme="white"
                    icon="check"
                    loading={loading}
                    type="submit"
                  >
                    Save
                  </FvButton>
                </InputAdornment>
                <InputAdornment position="end">
                  <FvButton
                    icon="times"
                    iconClass="text-fv-orange-bright"
                    onClick={toggleEditing}
                    theme="plain"
                    className="pr-0"
                  />
                </InputAdornment>
              </>
            }
          />
        </ValidatedForm>
      )}
    </div>
  )
}

type ReadOnlyRefNumProps = {
  label: string
  value: string
  showCopy?: boolean
}
export const ReadonlyRefNum = (props: ReadOnlyRefNumProps) => {
  const { label, value, showCopy = true } = props
  const textDisplay = `${label} ${value || NOT_AVAILABLE}`
  return (
    <span>
      {textDisplay}
      {showCopy && <ClipboardButton value={value} />}
    </span>
  )
}
